import { createContext } from 'preact';
import { useContext, useState, useEffect, useCallback } from 'preact/hooks';
import { translations, languages } from './index.js';

const I18nContext = createContext(null);

// Get nested value from object using dot notation
function getNestedValue(obj, path) {
  const keys = path.split('.');
  let result = obj;
  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = result[key];
    } else {
      return undefined;
    }
  }
  return result;
}

// Replace placeholders like {days} with actual values
function interpolate(str, params = {}) {
  if (!str || typeof str !== 'string') return str;
  return str.replace(/\{(\w+)\}/g, (match, key) => {
    return params[key] !== undefined ? params[key] : match;
  });
}

export function I18nProvider({ children }) {
  // Initialize language from localStorage or browser preference
  const [locale, setLocaleState] = useState(() => {
    // Check localStorage first
    const stored = localStorage.getItem('language');
    if (stored && translations[stored]) {
      return stored;
    }

    // Check browser language
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && translations[browserLang]) {
      return browserLang;
    }

    // Default to English
    return 'en';
  });

  // Save language preference when it changes
  const setLocale = useCallback((newLocale) => {
    if (translations[newLocale]) {
      localStorage.setItem('language', newLocale);
      setLocaleState(newLocale);
    }
  }, []);

  // Translation function
  const t = useCallback((key, params = {}) => {
    // Try to get translation in current locale
    let value = getNestedValue(translations[locale], key);

    // Fallback to English if not found
    if (value === undefined && locale !== 'en') {
      value = getNestedValue(translations.en, key);
    }

    // Return key if nothing found (for debugging)
    if (value === undefined) {
      console.warn(`Missing translation: ${key}`);
      return key;
    }

    // Interpolate placeholders
    return interpolate(value, params);
  }, [locale]);

  // Get current language info
  const currentLanguage = languages.find(l => l.code === locale) || languages[0];

  const value = {
    locale,
    setLocale,
    t,
    languages,
    currentLanguage,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

export { languages };
export default I18nContext;
